import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Stack from '@mui/material/Stack';
import IconButton from '../IconButton';
import Iconify from '../Iconify';
import ExpandedRow from './ExpandedRow';
import TableNameCell from './TableNameCell';
import GenericTableCell from '../GenericTableCell';
import ConditionComponent from '../ConditionComponent';
import GenericCell from './GenericCell';
import { isDateString } from '../../utils/date.util';
import { formatDate } from '../../utils/formatTime';
import { PORTAL_ROUTE, WDAC_ROUTE } from '../../constants/routes';
import { useNavigate } from 'react-router-dom';
import { ROW_SOURCES, tableRowColor } from 'src/constants/table';
import { WDAC_APP_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/Win32Apps/constants';
import LoadingRow from './LoadingRow';
import { WDAC_PACKAGED_APP_PREVIOUS_PATH } from '../../pages/wdac-results/tabs/PackagedApps/constants';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { WDAC_CATALOG_INVENTORY_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/WdacCatalogInventory/constants';
import { WDAC_MANAGED_INSTALLERS_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/WdacManagedInstallers/constants';
import { WDAC_SCRIPT_EXECUTION_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/WdacScriptExecution/constants';

/**
 * @typedef RowProps
 * @property {Object} row
 */

/**
 * Row component.
 * @param {RowProps} props - props of component
 */

const Row = forwardRef(
  (
    {
      row,
      columnData,
      ActionComponent,
      hasActions = true,
      markedRows = [],
      changeMarkedItem,
      hasSelection = false,
      isClickable = false,
      renderCustomCell,
      rowClickHandler,
      onClickRow,
      source,
      isExpired,
      isUniqueRow = false,
      highlightText,
      withTwoColors = false,
      isLoadingRow = false,
      backgroundColor = '#736546',
      expandedRowProps,
      onExpandChange,
    },
    ref,
  ) => {
    const isRowActive = markedRows[row?.email];
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const onToggleOpen = useCallback(
      event => {
        event?.stopPropagation();
        setOpen(prevOpen => {
          const nextOpen = !prevOpen;
          if (onExpandChange) {
            onExpandChange(row.id, nextOpen);
          }
          return nextOpen;
        });
      },
      [onExpandChange, row.id],
    );

    const navigateToApp = useCallback(
      id => {
        navigate(`${PORTAL_ROUTE}/apps/${id}/general`);
      },
      [navigate],
    );

    const navigateToPackagedAppDetails = useCallback(
      id => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(WDAC_PACKAGED_APP_PREVIOUS_PATH, currentPath);
        navigate(`${PORTAL_ROUTE}/packaged-details/${id}/general`);
      },
      [navigate],
    );
    const navigateToManagedInstallerDetails = useCallback(
      id => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(WDAC_MANAGED_INSTALLERS_PREVIOUS_PATH, currentPath);
        navigate(`${PORTAL_ROUTE}/managed-installer-details/${id}/general`);
      },
      [navigate],
    );

    const navigateToPolicyDetails = useCallback(
      policyId => {
        navigate(`${PORTAL_ROUTE}/policy-details/${policyId}/general`);
      },
      [navigate],
    );

    const navigateToCatalogInventoryDetails = useCallback(
      catalogInventoryId => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(WDAC_CATALOG_INVENTORY_PREVIOUS_PATH, currentPath);
        navigate(`${PORTAL_ROUTE}/catalog-inventory-details/${catalogInventoryId}/details`);
      },
      [navigate],
    );

    const navigateToScriptExecutionDetails = useCallback(
      scriptExecutionId => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(WDAC_SCRIPT_EXECUTION_PREVIOUS_PATH, currentPath);

        navigate(`${PORTAL_ROUTE}/script-execution-details/${scriptExecutionId}/details`);
      },
      [navigate],
    );

    const navigateToDeviceDetails = useCallback(
      id => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(DEVICES_PREVIOUS_PATH, currentPath);
        navigate(`${PORTAL_ROUTE}/devices/${id}/general`);
      },
      [navigate],
    );

    const navigateToWDAC = useCallback(() => {
      navigate(WDAC_ROUTE);
    }, [navigate]);

    const navigateToWDACApp = useCallback(
      id => {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem(WDAC_APP_PREVIOUS_PATH, currentPath);
        navigate(`${PORTAL_ROUTE}/wdac-apps/${id}/general`);
      },
      [navigate],
    );

    const onChangeMarkedItem = useCallback(() => {
      const newMarkedRows = { ...markedRows };
      if (newMarkedRows[row?.email]) {
        delete newMarkedRows[row.email];
      } else {
        newMarkedRows[row.email] = row;
      }

      changeMarkedItem(newMarkedRows);
    }, [row, changeMarkedItem, markedRows]);

    const rowExpiredStyle = useMemo(() => {
      return isExpired ? { backgroundColor: '#636363', opacity: '0.5' } : {};
    }, [isExpired]);

    const uniqueRowStyle = useMemo(() => {
      return isUniqueRow ? { backgroundColor: `${backgroundColor} !important` } : {};
    }, [isUniqueRow, backgroundColor]);

    const renderCell = useCallback(
      (column, params) => {
        switch (column?.name || column) {
          case 'firstName':
            return (
              <GenericCell
                cellValue={row.firstName}
                isRowActive={params.isRowActive}
                {...column?.props}
              />
            );
          case 'environmentApplicationName':
            return (
              <TableNameCell
                key={row.environmentApplicationName}
                name={row.environmentApplicationName}
                imgSrc={row.icon}
                isRowActive={params.isRowActive}
                {...column?.props}
              />
            );
          default:
            return (
              <GenericTableCell
                highlightText={highlightText}
                key={column?.name || column}
                isNumber={column?.isNumber}
                value={
                  column?.shouldHideValue
                    ? ''
                    : !column?.isVersion && isDateString(row[column?.name || column])
                      ? formatDate(row[column?.name || column])
                      : row[column?.name || column]
                }
                styles={column?.styles || {}}
                {...column?.props}
              />
            );
        }
      },
      [row, highlightText],
    );

    const onRowClick = useCallback(
      event => {
        if (onClickRow) {
          onClickRow(row);

          return;
        }

        if (hasSelection) {
          onChangeMarkedItem(row);

          return;
        }

        if (source === ROW_SOURCES.apps || source === ROW_SOURCES.files) {
          onToggleOpen(event);
        }

        if (source === ROW_SOURCES.devices) {
          navigateToDeviceDetails(row.id || row.deviceId);
        }

        if (source === ROW_SOURCES.wdac) {
          navigateToWDAC();
        }

        if (source === ROW_SOURCES.wdacApps) {
          navigateToWDACApp(row.id);
        }

        if (source === ROW_SOURCES.app) {
          navigateToApp(row.id);
        }

        if (source === ROW_SOURCES.packagedApps) {
          navigateToPackagedAppDetails(row.id);
        }
        if (source === ROW_SOURCES.managedInstallations) {
          navigateToManagedInstallerDetails(row.id);
        }

        if (source === ROW_SOURCES.policy) {
          navigateToPolicyDetails(row.id);
        }

        if (source === ROW_SOURCES.catalogInventory) {
          navigateToCatalogInventoryDetails(row.id);
        }

        if (source === ROW_SOURCES.scriptExecution) {
          navigateToScriptExecutionDetails(row.scriptExecutionId || row.id);
        }
      },
      [
        onClickRow,
        hasSelection,
        onChangeMarkedItem,
        row,
        onToggleOpen,
        source,
        navigateToDeviceDetails,
        navigateToWDAC,
        navigateToWDACApp,
        navigateToApp,
        navigateToPolicyDetails,
        navigateToPackagedAppDetails,
        navigateToManagedInstallerDetails,
        navigateToCatalogInventoryDetails,
        navigateToScriptExecutionDetails,
      ],
    );

    return (
      <>
        {isLoadingRow ? (
          <TableRow>
            <LoadingRow colSpan={1} columnCount={6} />
          </TableRow>
        ) : (
          <TableRow
            ref={ref}
            onClick={onRowClick}
            sx={{
              backgroundColor: isRowActive ? '#7dff9221' : '#4B4B4B',
              opacity: isRowActive ? 0.7 : 1,
              color: isRowActive ? '#000' : '#DDDDDD',
              cursor: hasSelection || isClickable ? 'pointer' : 'default',
              ...rowExpiredStyle,
              ...(withTwoColors
                ? {
                    '&:nth-of-type(even)': {
                      backgroundColor: tableRowColor,
                    },
                  }
                : {}),
              ...uniqueRowStyle,
            }}>
            {columnData.map(column => {
              if (renderCustomCell) {
                return renderCustomCell(column, row);
              }

              return renderCell(column, {
                isRowActive,
              });
            })}
            <ConditionComponent
              condition={!ActionComponent && hasActions}
              renderElement={
                <TableCell>
                  <Stack direction="row" justifyContent="center">
                    <IconButton
                      aria-label="expand row"
                      onClick={onToggleOpen}
                      selected={open}
                      color="#DDDDDD"
                      shape="rounded">
                      <Iconify
                        icon={
                          open ? 'iconamoon:arrow-up-2-duotone' : 'iconamoon:arrow-right-2-duotone'
                        }
                        color="#DDDDDD"
                        width={24}
                        height={24}
                      />
                    </IconButton>
                  </Stack>
                </TableCell>
              }
            />
            {hasActions && !!ActionComponent && (
              <ConditionComponent
                condition={!!ActionComponent && hasActions}
                renderElement={<ActionComponent />}
              />
            )}
          </TableRow>
        )}
        {open && (
          <ExpandedRow
            key={row.id}
            source={source}
            applicationId={row.id}
            columnsOrder={columnData}
            onClick={rowClickHandler}
            row={row}
            expandedRowProps={expandedRowProps}
          />
        )}
      </>
    );
  },
);

export default memo(Row);
